import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { GridColumn, PaginationSetting } from '@grid/abstractions';
import { paginationSettings } from '@shared/constants/pagination-settings';
import { TableDataSortType } from '@shared/enums/table-data-sort-type';
import { FilterSessionManagerService } from '@shared/modules/prime-grid-filters/services/filter-session-manager.service';
import { FilterSessionService } from '@shared/modules/prime-grid-filters/services/filter-session.service';
import { InitialFilterLoaderService } from '@shared/modules/prime-grid-filters/services/initial-filter-loader.service';
import { PlainObject } from 'odata-query';
import { Subject } from 'rxjs';
import { GridState } from '../types/grid-state';
import { ODataQueryState } from '../types/odata-query-state';
import { PrimeGridFilterBuilderService } from './prime-grid-filter-builder.service';

@Injectable()
export class GridQueryBuilderService<TData> {
  private readonly initialFilterLoaderService = inject(
    InitialFilterLoaderService
  );
  private readonly filterSessionManager = inject(FilterSessionManagerService);
  private readonly filterSession = inject(FilterSessionService);
  private readonly queryBuilder = inject<PrimeGridFilterBuilderService<TData>>(
    PrimeGridFilterBuilderService
  );

  triggerQuery = new Subject<ODataQueryState<TData>>();
  sessionKey?: string;
  allFiltersLoaded: boolean = false;
  private extendedPagination = false;
  private paginationRestored = false;
  pagination: WritableSignal<GridState> = signal(paginationSettings);

  constructor() {
    this.queryBuilder.init({ count: true });
    this.initialFilterLoaderService.allFiltersLoaded$.subscribe(() => {
      this.allFiltersLoaded = true;
      this.firstTrigger();
    });
  }

  initialize(session?: string, extendedPagination = false): void {
    this.sessionKey = session;
    this.extendedPagination = extendedPagination;
    this.paginationRestored = false;
    this.allFiltersLoaded = false;
  }

  firstTrigger(): void {
    if (this.sessionKey && this.filterSession.hasSession) {
      const externalQuery = this.initialFilterLoaderService.getExternalQuery();
      this.queryBuilder.merge(externalQuery);

      this.restoreSessionPaginationOnce();
    }
    this.build();
  }

  build(): void {
    this.buildPagination();
    if (this.allFiltersLoaded || !this.sessionKey) {
      const query = this.queryBuilder.getValue();
      this.triggerQuery.next(query);
    }
  }

  updatePagination(
    pagination: Partial<PaginationSetting>,
    assignOnly?: boolean
  ): void {
    const nextState = this.extendedPagination
      ? this.normalizePagination(this.withoutIsFirstFlag(pagination))
      : this.withoutIsFirstFlag(pagination);

    this.pagination.update((state: PaginationSetting) =>
      this.mergePaginationState(state, nextState)
    );

    if (!assignOnly) {
      this.build();
    }
  }

  updateFilter(filter: PlainObject): void {
    this.queryBuilder.filter(filter);
    if (this.allFiltersLoaded) {
      this.build();
    }
  }

  updateOrder(column: GridColumn, orderByValue?: TableDataSortType): void {
    this.queryBuilder.orderBy(
      orderByValue
        ? `${(column.sortKey ?? column.key).replaceAll('.', '/')} ${orderByValue}`
        : ''
    );
    this.build();
  }

  buildPagination(): void {
    // Must run before persisting skip/top, otherwise the stored page is overwritten.
    this.restoreSessionPaginationOnce();

    if (this.extendedPagination) {
      const rowByPage = this.getRowByPage();
      const currentPage = this.getCurrentPage();

      this.queryBuilder.top(rowByPage).skip((currentPage - 1) * rowByPage);
    } else {
      this.queryBuilder
        .top(this.pagination().rowByPage)
        .skip(
          (this.pagination().currentPage === 0
            ? 0
            : this.pagination().currentPage - 1) * this.pagination().rowByPage
        );
    }

    if (this.sessionKey) {
      this.filterSessionManager.updateSkip(this.queryBuilder.skipValue);
      this.filterSessionManager.updateTop(this.queryBuilder.topValue);
    }
  }

  reset(): void {
    this.allFiltersLoaded = false;
    this.extendedPagination = false;
    this.paginationRestored = false;
    this.initialFilterLoaderService.reset();
    this.queryBuilder.reset();
  }

  private restoreSessionPaginationOnce(): void {
    if (
      this.paginationRestored ||
      !this.extendedPagination ||
      !this.sessionKey ||
      !this.filterSession.hasSession
    ) {
      return;
    }

    this.paginationRestored = true;
    this.restorePaginationFromSession();
  }

  private restorePaginationFromSession(): void {
    const sessionQuery = this.filterSession.getQuery();
    const rowByPage = Number(sessionQuery['top']);
    const skip = Number(sessionQuery['skip']);

    if (!Number.isFinite(rowByPage) || rowByPage <= 0) {
      return;
    }

    this.pagination.update(state => ({
      ...state,
      rowByPage,
      currentPage:
        Number.isFinite(skip) && skip >= 0
          ? Math.max(1, Math.floor(skip / rowByPage) + 1)
          : state.currentPage,
    }));
  }

  private normalizePagination(
    pagination: Partial<PaginationSetting>
  ): Partial<PaginationSetting> {
    const normalizedPagination = { ...pagination };

    if (normalizedPagination.rowByPage !== undefined) {
      normalizedPagination.rowByPage = this.getRowByPage(
        normalizedPagination.rowByPage
      );
    }

    if (normalizedPagination.currentPage !== undefined) {
      normalizedPagination.currentPage = this.getCurrentPage(
        normalizedPagination.currentPage
      );
    }

    return normalizedPagination;
  }

  private getRowByPage(value?: number | string): number {
    const rowByPage = Number(value ?? this.pagination().rowByPage);

    return Number.isFinite(rowByPage) && rowByPage > 0 ? rowByPage : 10;
  }

  private getCurrentPage(value?: number | string): number {
    const currentPage = Number(value ?? this.pagination().currentPage);

    return Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;
  }

  private withoutIsFirstFlag(
    pagination: Partial<PaginationSetting>
  ): Partial<PaginationSetting> {
    const result = { ...pagination };
    delete result.isFirst;
    return result;
  }

  private mergePaginationState(
    state: PaginationSetting,
    updates: Partial<PaginationSetting>
  ): PaginationSetting {
    return {
      totalItems: updates.totalItems ?? state.totalItems,
      rowByPage: updates.rowByPage ?? state.rowByPage,
      currentPage: updates.currentPage ?? state.currentPage,
    };
  }
}
